import React from 'react';
import ReactDOM from 'react-dom/client';
import {
    createBrowserRouter,
    createRoutesFromElements,
    Route,
    RouterProvider
} from 'react-router-dom';
import Hotkeys from 'react-hot-keys';
import * as Sentry from '@sentry/browser';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

import AppLayout from './appLayout';
import LoginForm from './login';
import Users from './admin/Users';
import UserForm from './admin/UserForm';
import Workspaces from './workspace/Workspaces';
import WorkspaceForm from './workspace/WorkspaceForm';
import CurationProfileForm from './curation_profile/CurationProfileForm';
import WorkspaceDetails from './workspace/WorkspaceDetails';
import CurationProfileDetails from './curation_profile/CurationProfileDetails';
import SuspiciousDeletes from './suspicious_deletes/SuspiciousDeletes';
import ProgressSpinner from './component/atoms/ProgressSpinner';
import Message from './component/Messages';
import {getUser, redirectToLogin} from './helpers/authentication';
import {concatUrl} from './helpers/url';
import history from './helpers/history';
import {getUrlParams, getUrlPathname} from './helpers/location';
import {postCall} from './helpers/request';
import GlobalContext from 'helpers/context';
import {ROOT_PATH} from './settings';
import ConfirmationDialog from './component/ConfirmationDialog';
import ChangePasswordForm from './admin/ChangePasswordForm';
import DatabaseStatus from './component/DatabaseStatus';
import ApplicationOffline from './component/molecule/ApplicationOffline';
import {THEME} from './theme';


class AppRoot extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            user: undefined,
            systemError: undefined,
            systemInfo: undefined,
            systemWarning: undefined,
            confirm_dialog_is_open: false,
            show_hot_key_help: false,
            infocon_width_enabled: false,
            applicationActive: true,
            pathName: getUrlPathname(),
            params: getUrlParams()
        };

        this.logout_url = concatUrl(window.ROOT_URL, 'api', 'logout');

        this.globalContext = {
            displayGlobalError: this.displayGlobalError,
            displayGlobalInfo: this.displayGlobalInfo,
            displayGlobalWarning: this.displayGlobalWarning,
            checkUserAccess: this.checkUserAccess,
            showConfirmationDialog: this.showConfirmationDialog,
            showHotKeyHelp: this.showHotKeyHelp,
            applicationMode: this.applicationMode
        };

        if (window.SENTRY_DNS && window.SENTRY_ENVIRONMENT){
            Sentry.init({
                dsn: window.SENTRY_DNS,
                environment: window.SENTRY_ENVIRONMENT,
                release: window.SENTRY_RELEASE,
                integrations: [Sentry.captureConsoleIntegration({
                    levels: ['warn', 'error']
                })]
            });
            Sentry.getCurrentScope().setTag("logger", "front-end");
        }
    }

    componentDidMount(){
        if(!this.state.user) {
            getUser(this.getUserCallback);
        }
        // redirect to ROOT_PATH if no path is defined
        if(history.location.pathname === '' || history.location.pathname === '/') {
            history.push(ROOT_PATH);
        }
    }

    componentDidUpdate(){
        if(!this.state.user) {
            if(history.location.pathname !== '/login') {
                getUser(this.getUserCallback);
            }else{
                redirectToLogin();
            }
        }
        // redirect to ROOT_PATH if no path is defined
        if(history.location.pathname === '' || history.location.pathname === '/') {
            history.push(ROOT_PATH);
        }
    }

    getUserCallback = (user) => {
        this.setState({
            user: user
        });
        if(user === null) {
            redirectToLogin();
        }
    };

    applicationMode = (applicationActive) => {
        this.setState({applicationActive: applicationActive});
    };

    switchHotKeyHelp = () => {
        this.setState({show_hot_key_help: !this.state.show_hot_key_help});
    };

    switchInfoconWidth = () => {
        this.setState({infocon_width_enabled: !this.state.infocon_width_enabled});
    };

    showHotKeyHelp = () => {
        return this.state.show_hot_key_help;
    };

    checkUserAccess = (conditionCallback=null) => {
        /*
        Check if the user is logged in and in case a user is available check also
        if the user fulfill the additional condition.
        If conditionCallback is given and the user does not fulfill the condition
        he will be redirected to the root.
        */
        const checkUserCondition = (user) => {

            if (user && conditionCallback !== null && !conditionCallback(user)){
                if(history.location.pathname !== ROOT_PATH) {
                    history.push(ROOT_PATH);
                }
            }

            // Change the state only in case something has changes
            let current_user_id = this.state.user ? this.state.user.id : -1;
            if(!user && user !== this.state.user || user && user.id !== current_user_id){
                this.setState({
                    user: user
                });
            }

        };
        if(this.state.applicationActive) {
            getUser(checkUserCondition);
        }
    };

    resetUserSessionFailed = () => {
        this.displayGlobalError('Backend service failure: Could not reset the user session on server side.');
    };

    resetUserSessionDone = () => {
        this.setState({
            user: null
        });
        window.USER = {};
    };

    resetUserSession = () => {
        this.setState({
            user: null
        });
        postCall(this.logout_url, null, this.resetUserSessionDone, this.resetUserSessionFailed);
    };

    loginCallBack = (data) => {
        this.setState({
            user: data.user,
        });
        window.USER = data.user;
    };

    displayGlobalError = (error) => {
        this.setState({
            systemError: error
        });
    };

    resetSystemError = () => {
        this.setState({
            systemError: undefined
        });
    };

    displayGlobalInfo = (info) => {
        this.setState({
            systemInfo: info
        });
    };

    resetSystemInfo = () => {
        this.setState({
            systemInfo: undefined
        });
    };

    displayGlobalWarning = (info) => {
        this.setState({
            systemWarning: info
        });
    };

    resetSystemWarning = () => {
        this.setState({
            systemWarning: undefined
        });
    };

    onDialogClose = (event) => {
        this.setState({
            confirm_dialog_is_open: false
        });
        if(this.state.confirm_dialog_on_close){
            this.state.confirm_dialog_on_close(event);
        }
    };

    onDialogConfirm = (event) => {
        if(this.state.confirm_dialog_on_confirm){
            this.state.confirm_dialog_on_confirm(event);
        }
        this.setState({
            confirm_dialog_is_open: false
        });
    };

    checkOnDialogConfirm = () => {
        if(this.state.confirm_dialog_on_confirm){
            return this.onDialogConfirm;
        }
    };

    showConfirmationDialog = (confirmationDialogConfig) => {
        confirmationDialogConfig['confirm_dialog_is_open'] = true;
        this.setState(confirmationDialogConfig);
    };

    updateHeader = () => {
        let newPathName = getUrlPathname();
        let newParams = getUrlParams();
        if(this.state.pathName !== newPathName || JSON.stringify(this.state.params) !== JSON.stringify(newParams)){
            this.setState({
                pathName: newPathName,
                params: newParams
            });
        }
    };

    manageMainContent() {
        if(!this.state.applicationActive) {
            return (
                <ApplicationOffline />
            );
        }
        if(this.state.user === undefined) {
            return (
                <ProgressSpinner />
            );
        }

        let router = createBrowserRouter(
                    createRoutesFromElements(
                        <Route history={history}>
                            {/*login*/}
                            <Route path="/login" element={<LoginForm callback={this.loginCallBack} />} />
                            <Route element={<AppLayout logoutCallBack={this.resetUserSession} user={this.state.user} pathName={this.state.pathName} params={this.state.params}/>}>

                                {/* user management */}
                                <Route path="/users" element={<Users updatedCallback={this.updateHeader}/>} />
                                {['/users/create', '/users/edit'].map((path, index) => <Route
                                 path={path} key={index} element={<UserForm updatedCallback={this.updateHeader}/>} />)}
                                <Route path="/change_password" element={<ChangePasswordForm updatedCallback={this.updateHeader}/>} />

                                {/* workspace */}
                                <Route path="/workspaces" element={<Workspaces updatedCallback={this.updateHeader}/>} />
                                {['/workspaces/create', '/workspaces/edit'].map((path, index) => <Route
                                 path={path} key={index} element={<WorkspaceForm updatedCallback={this.updateHeader}/>} />)}
                                <Route path="/workspaces/details" element={<WorkspaceDetails updatedCallback={this.updateHeader}/>} />

                                {/* curation profile */}
                                {['/workspaces/curation_profiles/create', '/workspaces/curation_profiles/edit'].map((path, index) => <Route
                                 path={path} key={index} element={<CurationProfileForm updatedCallback={this.updateHeader}/>} />)}
                                <Route path="/workspaces/curation_profiles/details" element={<CurationProfileDetails updatedCallback={this.updateHeader}/>} />

                                {/* suspicious deletes */}
                                <Route path="/suspicious_deletes" element={<SuspiciousDeletes updatedCallback={this.updateHeader}/>} />

                            </Route>
                        </Route>
                    )
                )
        return (
            <RouterProvider router={router}/>
        );
    }

    render() {
        let infocon_look_and_feel = this.state.infocon_width_enabled ? "infocon_width" : '';
        return (
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={THEME}>
                    <div className={infocon_look_and_feel}>
                        <CssBaseline/>
                        <GlobalContext.Provider value={this.globalContext}>
                        {this.state.systemError && <Message variant="error" message={this.state.systemError} closeCallBack={this.resetSystemError} dataBt="error_message"/>}
                        {this.state.systemInfo && <Message variant="success" autoHideDuration={5000} message={this.state.systemInfo} closeCallBack={this.resetSystemInfo} dataBt="info_message"/>}
                        {this.state.systemWarning && <Message variant="warning" message={this.state.systemWarning} closeCallBack={this.resetSystemWarning} dataBt="warning_message"/>}
                        {this.manageMainContent()}

                        <ConfirmationDialog
                            on_close={this.onDialogClose}
                            on_confirm={this.checkOnDialogConfirm()}
                            dialog_title={this.state.confirm_dialog_title}
                            dialog_text={this.state.confirm_dialog_text}
                            dialog_button_text={this.state.confirm_dialog_button_text}
                            confirm_dialog_is_open={this.state.confirm_dialog_is_open}
                            dialog_mode={this.state.confirm_dialog_mode}
                        />

                        <Hotkeys keyName="alt+h" onKeyDown={this.switchHotKeyHelp} />
                        <Hotkeys keyName="alt+." onKeyDown={this.switchInfoconWidth} />

                        <DatabaseStatus />
                        </GlobalContext.Provider>
                    </div>
                </ThemeProvider>
            </StyledEngineProvider>
        );
    }
}


const root = ReactDOM.createRoot(document.getElementById("app_root"));
root.render(
    <AppRoot />
);